import { useState, useEffect, useRef, useCallback } from "react";

const ORANGE = "#E8621A";

export default function Metronome() {
  const [bpm, setBpm] = useState(120);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentBeat, setCurrentBeat] = useState(-1);
  const [timeSignature, setTimeSignature] = useState(4);
  const [tapTimes, setTapTimes] = useState([]);

  const audioCtxRef = useRef(null);
  const nextBeatTimeRef = useRef(0);
  const currentBeatRef = useRef(0);
  const schedulerRef = useRef(null);
  const bpmRef = useRef(bpm);
  const timeSigRef = useRef(timeSignature);
  const isPlayingRef = useRef(false);

  useEffect(() => { bpmRef.current = bpm; }, [bpm]);
  useEffect(() => { timeSigRef.current = timeSignature; }, [timeSignature]);

  const getAudioCtx = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioCtxRef.current;
  };

  const playClick = useCallback((time, isAccent) => {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = isAccent ? 1000 : 750;
    gain.gain.setValueAtTime(isAccent ? 0.8 : 0.5, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.06);
    osc.start(time);
    osc.stop(time + 0.06);
  }, []);

  const scheduler = useCallback(() => {
    const ctx = getAudioCtx();
    const scheduleAhead = 0.1;

    while (nextBeatTimeRef.current < ctx.currentTime + scheduleAhead) {
      const beat = currentBeatRef.current;
      const isAccent = beat === 0;
      playClick(nextBeatTimeRef.current, isAccent);

      const scheduledTime = nextBeatTimeRef.current;
      const delay = (scheduledTime - ctx.currentTime) * 1000;
      const capturedBeat = beat;
      setTimeout(() => {
        setCurrentBeat(capturedBeat);
      }, Math.max(0, delay));

      const secondsPerBeat = 60.0 / bpmRef.current;
      nextBeatTimeRef.current += secondsPerBeat;
      currentBeatRef.current = (currentBeatRef.current + 1) % timeSigRef.current;
    }
  }, [playClick]);

  const start = useCallback(() => {
    const ctx = getAudioCtx();
    if (ctx.state === "suspended") ctx.resume();
    currentBeatRef.current = 0;
    nextBeatTimeRef.current = ctx.currentTime + 0.05;
    isPlayingRef.current = true;
    setIsPlaying(true);
    setCurrentBeat(0);
    schedulerRef.current = setInterval(scheduler, 25);
  }, [scheduler]);

  const stop = useCallback(() => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    setCurrentBeat(-1);
    if (schedulerRef.current) {
      clearInterval(schedulerRef.current);
      schedulerRef.current = null;
    }
  }, []);

  const toggle = () => {
    if (isPlaying) stop();
    else start();
  };

  const handleTap = () => {
    const now = Date.now();
    setTapTimes(prev => {
      const recent = [...prev, now].filter(t => now - t < 3000).slice(-8);
      if (recent.length >= 2) {
        const intervals = recent.slice(1).map((t, i) => t - recent[i]);
        const avg = intervals.reduce((a, b) => a + b) / intervals.length;
        const newBpm = Math.round(60000 / avg);
        setBpm(Math.min(240, Math.max(30, newBpm)));
      }
      return recent;
    });
  };

  const adjustBpm = (delta) => {
    setBpm(prev => Math.min(240, Math.max(30, prev + delta)));
  };

  // Cleanup on unmount
  useEffect(() => () => {
    if (schedulerRef.current) clearInterval(schedulerRef.current);
  }, []);

  // Restart scheduler when bpm changes mid-play
  useEffect(() => {
    if (isPlaying) {
      stop();
      setTimeout(start, 50);
    }
  }, [bpm]);

  const timeSigs = [2, 3, 4, 6];

  return (
    <div style={{
      flex: 1,
      background: "#000",
      color: "#edeae2",
      display: "flex",
      flexDirection: "column",
      fontFamily: "inherit",
    }}>

      {/* ── Scrollable content area ── */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px 16px",
      }}>

        {/* Beat indicators */}
        <div style={{ display: "flex", gap: 12, marginBottom: 36 }}>
          {Array.from({ length: timeSignature }).map((_, i) => (
            <div key={i} style={{
              width: 14, height: 14, borderRadius: "50%",
              background: currentBeat === i
                ? (i === 0 ? ORANGE : "#edeae2")
                : "#2a2a28",
              transition: "background 0.05s",
              boxShadow: currentBeat === i
                ? `0 0 12px ${i === 0 ? ORANGE : "#edeae2"}`
                : "none",
            }} />
          ))}
        </div>

        {/* BPM Display */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{
            fontSize: 88, fontWeight: 800,
            lineHeight: 1, color: "#edeae2",
            letterSpacing: "-4px",
          }}>{bpm}</div>
          <div style={{
            fontSize: 13, color: "#585450",
            letterSpacing: "0.15em", marginTop: 6,
            textTransform: "uppercase",
          }}>BPM</div>
        </div>

        {/* BPM Controls */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, width: "100%" }}>
          {[[-10, "−10"], [-1, "−1"], [1, "+1"], [10, "+10"]].map(([delta, label]) => (
            <button
              key={label}
              onClick={() => adjustBpm(delta)}
              style={{
                flex: 1,
                background: "#1a1a18", border: `1px solid #242220`,
                borderRadius: 10, padding: "10px 0",
                color: "#edeae2", fontSize: 13, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit",
              }}
            >{label}</button>
          ))}
        </div>

        {/* BPM Slider */}
        <input
          type="range"
          min={30} max={240}
          value={bpm}
          onChange={e => setBpm(Number(e.target.value))}
          style={{ width: "100%", marginBottom: 24, accentColor: ORANGE }}
        />

        {/* Time Signature */}
        <div style={{ width: "100%" }}>
          <div style={{
            fontSize: 10, fontWeight: 600, letterSpacing: "0.1em",
            color: "#585450", textTransform: "uppercase",
            marginBottom: 8, textAlign: "center",
          }}>Time Signature</div>
          <div style={{ display: "flex", gap: 6 }}>
            {timeSigs.map(sig => (
              <button
                key={sig}
                onClick={() => {
                  setTimeSignature(sig);
                  if (isPlaying) { stop(); setTimeout(start, 50); }
                }}
                style={{
                  flex: 1,
                  paddingTop: 9, paddingBottom: 9,
                  borderRadius: 10,
                  border: `1.5px solid ${timeSignature === sig ? ORANGE : "#242220"}`,
                  background: timeSignature === sig ? "rgba(232,98,26,0.1)" : "transparent",
                  color: timeSignature === sig ? ORANGE : "#585450",
                  fontSize: 14, fontWeight: 700,
                  cursor: "pointer", fontFamily: "inherit",
                  transition: "color 0.12s, border-color 0.12s, background 0.12s",
                }}
              >{sig}/4</button>
            ))}
          </div>
        </div>

      </div>

      {/* ── Bottom-pinned buttons — same padding as Tuner ── */}
      <div style={{ padding: "4px 16px 28px", flexShrink: 0 }}>
        <button
          onClick={handleTap}
          style={{
            width: "100%",
            background: "#1a1a18", border: `1px solid #242220`,
            borderRadius: 12, padding: "15px 0",
            color: "#edeae2", fontSize: 15, fontWeight: "600",
            cursor: "pointer", marginBottom: 10,
            letterSpacing: "0.05em", fontFamily: "inherit",
          }}
        >Tap Tempo</button>

        <button
          onClick={toggle}
          style={{
            width: "100%",
            background: isPlaying ? "#161614" : "rgba(232,98,26,0.1)",
            border: `1px solid ${isPlaying ? "#242220" : ORANGE}`,
            borderRadius: 12, padding: "15px 0",
            color: isPlaying ? "#585450" : ORANGE,
            fontSize: 15, fontWeight: "600",
            cursor: "pointer", fontFamily: "inherit",
            transition: "background 0.15s, border-color 0.15s, color 0.15s",
          }}
        >{isPlaying ? "Stop" : "Tap to start"}</button>
      </div>

    </div>
  );
}
